import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useFetch } from "@hooks/useFetch";
import { IAddress, IUserAddress } from "@interface/customerDetails/IAddress";
import { getEircodeFinderRoute } from "@api-routes/addressFinder";
import type { FieldErrors, FieldValues, UseFormMethods } from "react-hook-form";
import { removeWhitespace } from "@utils/stringUtils";
import { capitalizeWord } from "@utils/addressFormatter";

interface IParams<TFieldValues extends FieldValues> {
    addressForm: UseFormMethods<TFieldValues> & Partial<UseFormMethods>;
    prefix?: string;
}

export const useSimpleEirCode = <TFieldValues extends FieldValues>(props: IParams<TFieldValues>) => {
    const { addressForm, prefix } = props;
    const { t } = useTranslation(["address", "common"]);

    const formErrors = (prefix ? addressForm.errors[prefix] : addressForm.errors) as FieldErrors<IUserAddress>;
    const namePrefix = prefix ? `${prefix}.` : "";
    const idPrefix = prefix ? `${prefix}-` : "";

    const [eircodeAddressList, setEircodeAddressList] = useState<IAddress[]>();

    const [getEircodeAddressListState, { get }] = useFetch();
    const eirCodeValue = useWatch<string>({
        name: `${namePrefix}code`,
        control: addressForm.control,
    });

    const submitEircode = async () => {
        setEircodeAddressList(undefined);

        const isEircodeValid = await addressForm.trigger(`${namePrefix}code`);

        if (isEircodeValid && eirCodeValue) {
            const eirCodeFormatted = eirCodeValue.toUpperCase();
            get<{ addresses: IAddress[] }>(getEircodeFinderRoute(eirCodeFormatted), { isSecured: false }).then(
                (result) => {
                    if (result?.addresses) {
                        const { addresses } = result;
                        setEircodeAddressList(addresses);

                        if (addresses.length >= 1) {
                            fillAddress(addresses[0]);
                        }
                    }
                }
            );
        }
    };

    const fillAddress = (newAddress: IAddress) => {
        addressForm.setValue(`${namePrefix}addressLine1`, capitalizeWord(newAddress.addressLine1));
        addressForm.setValue(
            `${namePrefix}addressLine2`,
            newAddress.addressLine2 ? capitalizeWord(newAddress.addressLine2) : ""
        );
        addressForm.setValue(
            `${namePrefix}addressLine3`,
            newAddress.addressLine3 ? capitalizeWord(newAddress.addressLine3) : ""
        );
        addressForm.setValue(`${namePrefix}town`, capitalizeWord(newAddress.town));
        addressForm.setValue(`${namePrefix}county`, newAddress.county);
        addressForm.setValue(`${namePrefix}code`, newAddress.eircode ?? "");
    };

    const onChangeEircode = (value: string) => {
        addressForm.setValue(`${namePrefix}addressLine1`, "");
        addressForm.setValue(`${namePrefix}addressLine2`, "");
        addressForm.setValue(`${namePrefix}addressLine3`, "");
        addressForm.setValue(`${namePrefix}town`, "");
        addressForm.setValue(`${namePrefix}county`, "");
        addressForm.setValue(`${namePrefix}code`, removeWhitespace(value));

        setEircodeAddressList(undefined);
    };

    let errorMessage = "";

    if (getEircodeAddressListState.error) {
        errorMessage = t("common:genericError");
    } else if (eircodeAddressList && eircodeAddressList.length === 0) {
        errorMessage = t("address:noAddressFound");
    }

    return {
        formErrors,
        namePrefix,
        idPrefix,
        eirCodeValue,
        errorMessage,
        getEircodeAddressListState,
        onChangeEircode,
        submitEircode,
    };
};
